import type React from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useEffect } from 'react';

import type { ColorName } from 'styles/config/colors';

import InputWrapper from '../InputWrapper';
import { BodyStrong } from '../LegacyText';
import * as Styled from './Slider.styled';

export interface SliderProps extends InputHTMLAttributes<HTMLInputElement> {
  minValue?: number;
  maxValue?: number;
  segments?: { label: string; value: string | number }[];
  backgroundColor?: ColorName;
  thumbBackgroundColor?: ColorName;
  onHoverThumbBackgroundColor?: ColorName;
  sliderWidth?: string;
  mobileWidth?: boolean;
  showValueInInput?: boolean;
  disabled?: boolean;
  label?: ReactNode;
  help?: string;
  knobTooltipOverride?: string | false;
  hideKnobTooltip?: boolean;
  sliderTooltipPosition?: Styled.SliderTooltipPosition;
  minValueLabel?: (value: number) => string | null | undefined;
  maxValueLabel?: (value: number) => string | null | undefined;
  knobTooltip?: (value: number) => string | null | undefined;
  'data-test-id-tooltip'?: string;
  errors?: string[];
}

const calculateOffset = (value: number, minValue: number, maxValue: number) => {
  return ((value - minValue) / (maxValue - minValue)) * 100;
};

const calculateTooltipPosition = (
  value: number,
  minValue: number,
  maxValue: number,
) => {
  const range = maxValue - minValue;
  const position = ((value - minValue) / range) * 100;
  const positionOffset =
    Math.round((Styled.THUMB_WIDTH * position) / 100) - Styled.THUMB_WIDTH / 2;
  return `calc(${position}% - ${positionOffset}px)`;
};

export const Slider = forwardRef(
  (
    {
      label,
      help,
      backgroundColor = 'sliderTrackActive',
      thumbBackgroundColor = 'sliderThumb',
      onHoverThumbBackgroundColor = 'sliderThumb',
      sliderWidth,
      mobileWidth = false,
      showValueInInput = false,
      disabled = false,
      onChange,
      segments,
      value: propsValue,
      defaultValue: propsDefaultValue,
      minValue: propsMinValue,
      maxValue: propsMaxValue,
      hideKnobTooltip = false,
      sliderTooltipPosition,
      minValueLabel: propsMinValueLabelFn,
      maxValueLabel: propsMaxValueLabelFn,
      knobTooltip: propsKnobTooltipFn,
      'data-test-id-tooltip': tooltipTestId = 'tooltip-text',
      errors,
      ...restProps
    }: SliderProps,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const getVariables = () => {
      if (segments) {
        const defaultValueIndex = segments.findIndex(
          segment => propsDefaultValue === segment.value,
        );
        const valueIndex = segments.findIndex(
          segment => propsValue === segment.value,
        );
        return {
          range: [0, segments.length - 1],
          defaultValue: defaultValueIndex,
          value: valueIndex,
          knobTooltip: segments[valueIndex]?.label,
          minValueLabel: segments[0].label,
          maxValueLabel: segments[segments.length - 1].label,
        };
      }
      const minValue = propsMinValue || 0;
      const maxValue = propsMaxValue || 0;
      const currentValue = propsValue as number;
      return {
        range: [minValue, maxValue],
        defaultValue: propsDefaultValue as number,
        value: currentValue,
        knobTooltip: propsKnobTooltipFn
          ? propsKnobTooltipFn(currentValue)
          : currentValue,
        minValueLabel: propsMinValueLabelFn
          ? propsMinValueLabelFn(minValue)
          : minValue,
        maxValueLabel: propsMaxValueLabelFn
          ? propsMaxValueLabelFn(maxValue)
          : maxValue,
      };
    };

    const {
      range: [minValue, maxValue],
      defaultValue,
      value,
      knobTooltip,
      minValueLabel,
      maxValueLabel,
    } = getVariables();

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      if (inputValue === '') {
        e.target.value = String(value || '');
        return;
      }

      const numericValue = Number(inputValue);

      // Validate the input value
      if (
        !isNaN(numericValue) &&
        numericValue >= minValue &&
        numericValue <= maxValue
      ) {
        onChange?.(e as any);
      } else {
        e.target.value = String(value || '');
      }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        (e.target as HTMLInputElement).blur();
      }
    };

    useEffect(() => {
      if (
        process.env.NODE_ENV !== 'production' &&
        !segments &&
        (minValue === undefined || maxValue === undefined)
      ) {
        console.warn('[Slider] minValue and maxValue must be provided');
      }
    }, [segments, minValue, maxValue]);

    return (
      <InputWrapper
        data-test-id-tooltip={tooltipTestId}
        errors={errors}
        help={help}
        label={label}
      >
        <Styled.SliderRow $disabled={disabled} $sliderWidth={sliderWidth}>
          <BodyStrong className="minLabel">{minValueLabel}</BodyStrong>
          <Styled.RangeControl $mobileWidth={mobileWidth} className="slider">
            {segments && (
              <Styled.Segments>
                {Array.from({ length: maxValue - minValue }).map((_, index) => (
                  <Styled.Segment
                    // biome-ignore lint/suspicious/noArrayIndexKey: existing
                    key={index}
                    $disabled={disabled}
                    $fillColor={backgroundColor}
                    $isFilled={value > index}
                  />
                ))}
              </Styled.Segments>
            )}
            <Styled.Slider
              ref={ref}
              $backgroundColor={backgroundColor}
              $onHoverThumbBackgroundColor={onHoverThumbBackgroundColor}
              $sliderWidth={sliderWidth}
              $thumbBackgroundColor={thumbBackgroundColor}
              $tooltipOnHover={!!disabled}
              $value={calculateOffset(value, minValue, maxValue)}
              $withTransparentBackground={!!segments}
              defaultValue={defaultValue}
              disabled={disabled}
              max={maxValue}
              min={minValue}
              type="range"
              value={value}
              onChange={onChange}
              {...restProps}
            />
            {!hideKnobTooltip && (
              <Styled.SliderTooltip
                $leftPosition={calculateTooltipPosition(
                  value,
                  minValue,
                  maxValue,
                )}
                $position={sliderTooltipPosition}
                $sliderTooltipLineHeight={segments ? 'tight' : 'loose'}
                $sliderTooltipWidth={segments ? 'auto' : 'fixed'}
                name="rangeVal"
              >
                {knobTooltip}
              </Styled.SliderTooltip>
            )}
          </Styled.RangeControl>
          <BodyStrong className="maxLabel">{maxValueLabel}</BodyStrong>
          {showValueInInput && !segments && (
            <div className="inputField">
              <Styled.ValueIndicator
                key={`slider-${value}`}
                defaultValue={String(value || '')}
                disabled={disabled}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
              />
            </div>
          )}
        </Styled.SliderRow>
      </InputWrapper>
    );
  },
);
